import { CognitoUser } from '@aws-amplify/auth';
import { Auth } from 'aws-amplify';
import React, { useEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { Location, useLocation, useNavigate } from 'react-router-dom';

import { Checkbox, Link, Spacer, Text } from '../../../components';
import Form, { FormData } from '../../../components/Form/Form';
import { useAppContext } from '../../../contexts/AppContext';
import { useExtendedTranslation } from '../../../hooks/useExtendedTranslation';
import UnauthenticatedLayout from '../../../overwrites/views/layouts/UnauthenticatedLayout/UnauthenticatedLayout';
import { EmailRegex, querystring } from '../../../utils/RouteHelper';
import { FormContainer, MainButton, ReSendLink } from '../auth.styles';
import SignupHeader from '../shared/SignupHeader';
import {
  BottomContainer,
  CodeInstruction,
  CodeInstructionEmail,
  ForgotYourPasswordLink,
  RememberForgotContainer,
} from './LoginPage.styles';

enum FormKey {
  Email = 'email',
  Password = 'password',
  MFACode = 'mfaCode',
}

export interface LoginStateProps {
  email: string;
  isMFAStep?: boolean;
}

export default function LoginPage() {
  const { showError, reloadAuthInfo, showAlert } = useAppContext();
  const [loading, setLoading] = useState(false);
  const { state, search } = useLocation() as Location & { state: LoginStateProps };
  const [isMFAStep, setIsMFAStep] = useState<boolean>(state?.isMFAStep || false);
  const [isResendBlocked, setIsResendBlocked] = useState(false);
  const [timer, setTimer] = useState<number>(0);
  const [user, setUser] = useState<CognitoUser>(null);
  const navigate = useNavigate();
  const { t } = useExtendedTranslation();
  const formRef = useRef(null);

  const isSignUpAvailable = process.env.REACT_APP_SIGNUP_WITHOUT_INVITE_ENABLE === 'true';

  const resendAgainDelayTime = 10000;

  const [formData, setFormData] = useState<FormData>({
    [FormKey.Email]: state?.email,
  });

  const validate = (data: FormData, errors: any) => {
    const email = data[FormKey.Email] as string;
    if (!EmailRegex.test(email)) {
      errors[FormKey.Email] = t('auth.error.invalidEmail');
      return false;
    }

    return true;
  };

  const handleSubmit = (data: FormData) => {
    void (async () => {
      const sanitizedEmail = (data[FormKey.Email] as string).toLowerCase().trim();
      const password = data[FormKey.Password] as string;
      try {
        setLoading(true);
        const user = (await Auth.signIn(sanitizedEmail, password)) as CognitoUser;
        setUser(user);
        setLoading(false);
        setIsMFAStep(true);
      } catch (e: any) {
        if (e.code === 'UserNotConfirmedException') {
          navigate('/verify', { state: { email: sanitizedEmail, password } });
        } else {
          showError(e?.message);
          setLoading(false);
        }
      }
    })();
  };

  const handleCodeConfirmation = (data: FormData) => {
    setLoading(true);
    void (async () => {
      const code = data[FormKey.MFACode] as string;
      try {
        const response = await Auth.sendCustomChallengeAnswer(user, code);

        if (!response?.signInUserSession?.idToken?.jwtToken) {
          showError(t('auth.mfa.wrongCode'));
        } else {
          const redirect = querystring('redirect') ? encodeURIComponent(querystring('redirect')) : '';
          navigate(`.${redirect ? `?redirect=${redirect}` : ''}`);
          await reloadAuthInfo();
        }
      } catch (e: any) {
        if (e.code === 'NotAuthorizedException') {
          showError(t('auth.mfa.errorOcurred'));
          setUser(null);
          setFormData({ ...formData, [FormKey.MFACode]: '' });
          setIsMFAStep(false);
        } else {
          showError(e.message);
        }
      } finally {
        setTimer(resendAgainDelayTime / 1000);
        setTimeout(() => {
          setIsResendBlocked(false);
        }, resendAgainDelayTime);
        setLoading(false);
      }
    })();
  };

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
    return () => null;
  }, [timer]);

  const resendCode = (data: FormData) => {
    void (async () => {
      if (isResendBlocked) return;
      const sanitizedEmail = (data[FormKey.Email] as string).toLowerCase().trim();
      const password = data[FormKey.Password] as string;
      const user = (await Auth.signIn(sanitizedEmail, password)) as CognitoUser;
      setUser(user);
      showAlert(t('auth.verify.codeSent'));
      setTimer(resendAgainDelayTime / 1000);
    })();
  };

  React.useEffect((): any => {
    const paramEmail = querystring('email', search);
    if (paramEmail) setFormData({ ...formData, [FormKey.Email]: paramEmail });
  }, [search]);

  const handleGoBack = () => {
    setIsMFAStep(false);
    setUser(null);
    setFormData({ ...formData, [FormKey.MFACode]: '' });
  };

  return (
    <UnauthenticatedLayout
      title={isMFAStep ? t('auth.mfa.title') : t('auth.signIn.title')}
      header={<SignupHeader />}
      pageContainerMaxWidth="26rem"
      showBack={isMFAStep}
      onGoBack={handleGoBack}
    >
      <Form
        ref={formRef}
        data={formData}
        validateFn={!isMFAStep && validate}
        onSubmit={isMFAStep ? handleCodeConfirmation : handleSubmit}
      >
        {({ input, data, isRequiredDataFilled }) => (
          <FormContainer>
            {!isMFAStep ? (
              <>
                {input({
                  dataKey: FormKey.Email,
                  required: true,
                  label: t('auth.common.email.label'),
                  errorRequired: t('auth.error.email'),
                  placeholder: t('auth.common.email.placeholder'),
                })}
                {input({
                  dataKey: FormKey.Password,
                  required: true,
                  type: 'password',
                  label: t('auth.common.password.label'),
                  errorRequired: t('auth.error.password'),
                  placeholder: t('auth.common.password.placeholder'),
                })}
              </>
            ) : (
              <>
                <CodeInstruction>
                  <Trans
                    i18nKey="auth.signIn.codeInstruction"
                    values={{ email: data[FormKey.Email] }}
                    components={{ black: <CodeInstructionEmail /> }}
                  />
                </CodeInstruction>
                {input({
                  dataKey: FormKey.MFACode,
                  required: true,
                  autoFocus: true,
                  label: t('auth.mfa.confirmationCode'),
                  placeholder: t('auth.mfa.placeholder'),
                })}
              </>
            )}
            {!isMFAStep && (
              <RememberForgotContainer>
                <Checkbox defaultSelected={true}>{t('auth.signIn.rememberMe')}</Checkbox>
                <ForgotYourPasswordLink onClick={() => navigate('/recovery-mail')}>
                  {t('auth.signIn.forgotYourPassword')}
                </ForgotYourPasswordLink>
              </RememberForgotContainer>
            )}
            <MainButton
              disabled={isMFAStep && !isRequiredDataFilled}
              loading={loading}
              onPress={() => formRef.current.submit()}
            >
              {isMFAStep ? t('auth.mfa.verify') : t('auth.signIn.logIn')}
            </MainButton>
            {isMFAStep ? (
              <ReSendLink onClick={() => resendCode(data)}>
                {t('auth.mfa.link')} {timer > 0 ? `${t('common.in')}: ${timer}` : ''}
              </ReSendLink>
            ) : (
              isSignUpAvailable && (
                <BottomContainer>
                  <Text>{t('auth.signIn.dontHaveAccount')}</Text>
                  <Spacer x={0.3} />
                  <Link onClick={() => navigate('/signup')}>{t('auth.common.signUp')}</Link>
                </BottomContainer>
              )
            )}
          </FormContainer>
        )}
      </Form>
    </UnauthenticatedLayout>
  );
}
